import "./EmailTemplate.styles.css";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import React, { useEffect, useState } from "react";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import { DataTable } from "../../components/molecules/DataTable";
import { Drawer, FormControlLabel } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { PageHeader } from "../../components/organisms/PageHeader";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { getListEmailTemplate } from "../../api/emailTriage";
import { ActionsColumn } from "../../components/molecules/StyledMenu";
import { EmailTemplateProps, IEmailTemplate } from "./EmailTemplate.props";
import LeavePageDialog from "../../components/organisms/LeavePageDialog/LeavePageDialog";
import { useCallbackPrompt } from "../../hooks/useCallbackPrompt";
import { Toast } from "../../components/atoms/Toast";
import Box from "@mui/material/Box";
import { toPages } from "../../utils/common";
import { Modal } from "../../components/atoms/Modal";
import ChangeWithZone from "../../hoc/ChangeWithZone";
import { SettingFull } from "../Settings";
import { useTranslation } from "react-i18next";
import {DATA_TABLE_ITEMS_PER_PAGE} from "../../constants/contants";

const GreenSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: "#008545",
    '&:hover': {
      backgroundColor: "#B3DAC7",
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: "#B3DAC7",
  },
}));

type TemplateData = {
  id: string;
  status: "active" | "inactive";
  [key: string]: any;
};

type CheckMap = { [id: string]: boolean };

function getChangedTemplatesUpdated(
  templateData: TemplateData[],
  check: CheckMap
): TemplateData[] {
  const toStr = (b: boolean) => (b ? "active" : "inactive");
  const changed: TemplateData[] = [];
  for (const t of templateData) {
    if (Object.prototype.hasOwnProperty.call(check, t.id)) {
      const newStatus = toStr(check[t.id]);
      if (t.status !== newStatus) {
        changed.push({ id: t.id, status: newStatus });
      }
    }
  }
  return changed;
}

const PERPAGES = 10;

const EmailTemplateFull: React.FC<EmailTemplateProps> = () => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const [initChecked, setInitChecked] = useState({})
  const [checked, setChecked] = useState({});
  const [isShowLoading, setShowLoading] = useState(false);
  const [templateData, setTemplateData] = useState([]);
  const [sortCol, setSortCol] = useState('type');
  const [sortDir, setSortDir] = useState('asc');
  const [isActionShow, setActionShow] = React.useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState<boolean>(false);
  const [toastValue, setToastValue] = useState({ type: '', message: '', isOpen: false, toastId: 0 });
  const [modalID, setModalID] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPages, setPerPages] = useState(PERPAGES);
  const [startPoint, setStartPoint] = useState(0);
  const [endPoint, setEndPoint] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(showLeaveDialog)
  const [openDelete, setOpenDelete] = useState(false);
  const [templateToBeDeleted, setTemplateToBeDeleted] = useState('');
  const [openUnsavedConfirm, setOpenUnsavedConfirm] = useState(false);
  const [actionTargetToOpen, setActionTargetToOpen] = useState<HTMLElement | null>(null);
  const [newEntryOpenPending, setNewEntryOpenPending] = useState(false);
  const [pendingPage, setPendingPage] = useState(currentPage);
  const [pendingPerPage, setPendingPerPage] = useState(PERPAGES);
  const processTemplateResponse = (res: any) => {
    if (res && res.status === 200) {
      const { list, endItemsPerPage, maxPage, startItemsPerPage, totalItems } = res.data;
      const templates = list || [];
      if (templates.length === 0 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
        return;
      }

      templates.forEach((item: any) => {
        if (item.type === "default") {
          item.disabled_actions = true;
        }
      });
      setTemplateData(templates);
      setTotalPages(maxPage);
      setTotalRows(totalItems);
      setStartPoint(startItemsPerPage);
      setEndPoint(endItemsPerPage);
      setShowLoading(false);
      let newChecked = { ...checked };
      templates.forEach((templ: IEmailTemplate) => {
        newChecked = { ...newChecked, [templ.id]: (templ.status === 'active') }
      });
      setInitChecked(newChecked);
      setChecked(newChecked);
    }
  }

  const getTemplatesTable = () => {
    getListEmailTemplate({
      action: "list",
      payload: { sort_col: sortCol, sort_dir: sortDir, page: currentPage, per_page: perPages }
    }).then((res: any) => {
      processTemplateResponse(res);
    }).catch(err => {
      console.error("err: ", err);
      setShowLoading(false);
    });
  }

  useEffect(() => {
    setShowLoading(true)
    getTemplatesTable()
  }, [perPages, currentPage, sortCol, sortDir]);

  const handlePerRowsChange = (e: any) => {
    const value = parseInt(e.target.value);
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setPendingPerPage(value);
    } else {
      setPerPages(value);
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page: number) => {
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setPendingPage(page);
    } else {
      setCurrentPage(page);
    }
  }

  const paginationComponent = () => {
    const perRowsPages = DATA_TABLE_ITEMS_PER_PAGE;
    const pageItems = toPages(totalPages);
    const nextDisabled = currentPage === pageItems.length || !totalRows ? 'disabled' : '';
    const previousDisabled = currentPage === 1 || !totalRows ? 'disabled' : '';

    return <nav className='pagination'>
      <div className='pagination-flex'>

        <span className='rowPerPages' style={{ marginRight: 8 }}>Rows per page</span>
        <select value={perPages} onChange={handlePerRowsChange} style={{ width: 59 }}>
          {
            perRowsPages.map(page => <option key={page} value={page}>{page}</option>)
          }
        </select>

        <span className='totalRows'
              style={{ marginRight: 32, marginLeft: 32 }}> {startPoint}-{endPoint} of {totalRows}  </span>

        <ul className="pagination-numbers">
          <li data-testid="first-page" onClick={() => handlePageChange(1)} className={'first-page ' + previousDisabled}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"
                 role="presentation">
              <path d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z"></path>
              <path fill="none" d="M24 24H0V0h24v24z"></path>
            </svg>
          </li>
          <li data-testid="previous-page" onClick={() => handlePageChange(currentPage - 1)}
              className={'previous-page ' + previousDisabled}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"
                 role="presentation">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"></path>
              <path d="M0 0h24v24H0z" fill="none"></path>
            </svg>
          </li>
          <li data-testid="next-page" onClick={() => handlePageChange(currentPage + 1)}
              className={'next-page ' + nextDisabled}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="24" viewBox="0 0 24 24" aria-hidden="true"
                 role="presentation">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path>
              <path d="M0 0h24v24H0z" fill="none"></path>
            </svg>
          </li>
          <li data-testid="last-page" onClick={() => handlePageChange(pageItems.length)}
              className={'last-page ' + nextDisabled}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="24" viewBox="0 0 24 24" aria-hidden="true"
                 role="presentation">
              <path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z"></path>
              <path fill="none" d="M0 0h24v24H0V0z"></path>
            </svg>
          </li>
        </ul>
      </div>
    </nav>
  }

  const changeStatusRule = (status: any, row: any) => {
    const isChecked = status.target.checked
    setChecked({
      ...checked,
      [row.id]: isChecked
    })
    setActionShow(true);
    setShowLeaveDialog(true);
  }

  const columns: GridColDef[] = [
    {
      field: 'name', headerName: t('fields.templates.name'), width: 300, sortable: true,
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      }
    },
    {
      field: 'desc', headerName: t('fields.templates.description'), flex: 1, maxWidth: 600, sortable: false,
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      }
    },
    {
      field: 'type', headerName: t('fields.templates.type'), flex: 1, maxWidth: 400, sortable: true,
      renderCell: ({ value }) => {
        return (
          <Box className={`template_type template_type_${value}`}>
            <span>{value ? `${value[0].toUpperCase()}${value.slice(1)}` : ''}</span>
          </Box>
        )
      },
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      }
    },
    {
      field: 'status', headerName: t('fields.templates.status'), width: 150, sortable: true,
      renderCell: (row: any) => {
        return <FormControlLabel className={`status-col ${checked[row.id] ? 'active' : 'inactive'}`} control={
          <GreenSwitch checked={checked[row.id]} onChange={(status: any) => changeStatusRule(status, row)}/>
        } label={checked[row.id] ? 'Active' : 'Inactive'}/>
      }
    },
  ];

  const handleClickItem = (event: any) => {
    const fields = ['status']
    if(!fields.includes(event.field))  {
      navigate('/settings/email-templates/' + event.id)
    }
  }

  const onSort = (model: any) => {
    setSortDir(model?.length !== 0 ? model[0].sort : 'asc');
    setSortCol(model?.length !== 0 ? model[0].field : 'type');
    setActionShow(false)
    setShowLeaveDialog(false)
  }

  const handleDiscard = () => {
    setActionShow(false)
    setChecked(initChecked)
    setShowLeaveDialog(false);
  }

  const handleSaveChange = () => {
    setShowLoading(true)
    const updatedItems = getChangedTemplatesUpdated(templateData, checked)
    getListEmailTemplate({
      action: 'update_templates_status',
      payload: { templates: updatedItems }
    }).then((response: any) => {
      if (response.status === 200) {
        setModalID(modalID + 1)
        setToastValue({ type: 'success', message: t('messages.genericSuccess'), isOpen: true, toastId: modalID })
        setInitChecked(checked)
        getTemplatesTable()
        setActionShow(false)
        setShowLoading(false)
        setShowLeaveDialog(false);
      } else {
        setModalID(modalID + 1);
        setShowLoading(false)
        setToastValue({ type: 'error', message: t('messages.genericError'), isOpen: true, toastId: modalID });
      }
    })
  }

  const saveChangeUI = (
    <Box className={'save-change-content'}
         sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px 0' }}>
      <div className={'btn discard-btn'}>
        <Button variant="outlined" onClick={handleDiscard}>{t("actions.discard")}</Button>
      </div>
      <div className={'btn save-btn'}>
        <Button variant="contained" onClick={handleSaveChange}>{t("actions.saveChanges")}</Button>
      </div>
    </Box>
  )

  const showActionsDropdown = (event: React.MouseEvent<HTMLElement>) => {
    const templateId: any = event.currentTarget.getAttribute('data-rowid');
    if (isActionShow) {
      setOpenUnsavedConfirm(true);
      setActionTargetToOpen(event.currentTarget);
    } else {
      setTemplateToBeDeleted(templateId);
      setAnchorEl(event.currentTarget);
    }
  };

  const hideActionsDropdown = () => {
    const gridRows = document.querySelectorAll('.MuiDataGrid-row');
    if (gridRows) {
      for (let i = 0; i < gridRows.length; i++) {
        gridRows[i].classList.remove('focusing');
      }
    }
    setAnchorEl(null);
  };

  const handleActionDelete = (params?: any, page?: string) => {
    setAnchorEl(null);
    setOpenDelete(true);
  };

  const handleAddNew = () => {
    if (isActionShow) {
      setNewEntryOpenPending(true);
      setOpenUnsavedConfirm(true);
    } else {
      navigate('/settings/email-templates/new');
    }
  };

  const handleCloseDeleteModal = () => {
    setOpenDelete(false);
  }

  const handleDeleteTemplate = () => {
    setShowLoading(true)
    getListEmailTemplate({
      action: "delete",
      payload: { template_id: templateToBeDeleted }
    }).then((res: any) => {
      setToastValue({
        type: 'success',
        message: t('messages.templates.deleteSuccess'),
        isOpen: true,
        toastId: modalID
      })
      getTemplatesTable()
    }).catch(err => {
      console.error("err: ", err);
      setShowLoading(false);
    });
  }

  const handleContinueWhenUnsaved = () => {
    if (newEntryOpenPending) {
      navigate('/settings/email-templates/new');
    }
    setAnchorEl(actionTargetToOpen);
    if (pendingPage !== currentPage) {
      setCurrentPage(pendingPage);
    }
    if (pendingPerPage !== perPages) {
      setPerPages(pendingPerPage);
    }
    setOpenUnsavedConfirm(false);
    setActionShow(false);
    setShowLeaveDialog(false);
    setChecked(initChecked);
  };

  const handleDiscardWhenUnsaved = () => {
    setActionTargetToOpen(null);
    setPendingPage(currentPage);
    setPendingPerPage(PERPAGES);
    setOpenUnsavedConfirm(false);
  };

  return (
    <div className="email-template-page page-content-wrapper">
      <PageHeader
        pageTitle="Email Templates"
        infoPage={t('tooltips.templates.listPageHeader')}
        tooltipStyle={{ minWidth: '650px' }}
        children={
          <Button
            className="new-template-btn"
            data-testid="new-template-btn"
            onClick={() => handleAddNew()}
            variant="contained"
            size="large"
          >
            <AddIcon
              style={{ fontSize: '20px' }}/>
            <span style={{ paddingLeft: '8px' }}>{t('actions.templates.new')}</span>
          </Button>
        }
      />
      <Stack className="page-content-body">
        <div className="email-template-content">
          <div className="template-datatable">
            <DataTable
              columns={columns}
              data={templateData}
              actionsColumn={<ActionsColumn
                open={open}
                anchorEl={anchorEl}
                hideActionsDropdown={hideActionsDropdown}
                menuData={templateData}
                isTemplate
                showActionsDropdown={showActionsDropdown}
                handleDelete={handleActionDelete}
              />}
              pagination
              CustomPagination={paginationComponent}
              loadingTable={isShowLoading}
              onRowDoubleClick={handleClickItem}
              onSortModelChange={(model: any) => onSort(model)}
              initialSortCol={'type'}
              initialSortDir={'asc'}
            />
          </div>
        </div>
      </Stack>
      <Drawer
        variant="persistent"
        anchor="bottom"
        open={isActionShow}
        onClose={() => setActionShow(false)}
        className={'save-change-container'}
        children={saveChangeUI}
      ></Drawer>
      <Toast
        isOpen={toastValue.isOpen}
        type={toastValue.type}
        message={toastValue.message}
        toastId={toastValue.toastId}
        autoHideDuration={2000}
      />
      <LeavePageDialog
        showDialog={showPrompt}
        confirmNavigation={confirmNavigation}
        cancelNavigation={cancelNavigation}
      />
      <Modal
        idModal="modal-delete-template"
        openDialog={openDelete}
        variant="delete"
        title="Delete template"
        content="Are you sure to delete this email template?"
        actionAgree={/* istanbul ignore next */() => {
          handleDeleteTemplate()
          /* istanbul ignore next */
          handleCloseDeleteModal()
        }}
        actionCancel={handleCloseDeleteModal}
        textAgree={t('actions.delete')}
        onClose={handleCloseDeleteModal}
      />
      <Modal
        idModal="modal-unsaved-confirmation"
        openDialog={openUnsavedConfirm}
        variant="warning"
        title={t('pageTitles.threatExceptions.unsavedChanges')}
        content={t('messages.threatExceptions.unsavedChanges')}
        actionAgree={handleContinueWhenUnsaved}
        actionCancel={handleDiscardWhenUnsaved}
        textAgree={t('actions.threatExceptions.discardChanges')}
        textCancel={t('actions.cancel')}
        onClose={handleDiscardWhenUnsaved}
      />
    </div>
  );
};

const EmailTemplate = ChangeWithZone(EmailTemplateFull);
export default EmailTemplate;
